import { useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import {
  Inbox,
  Search,
  RefreshCw,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { accessRequestsApi } from '../../../api/client';
import type { AccessRequest, AccessRequestStatus } from '../../../api/client';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Status Badge ───────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<AccessRequestStatus, string> = {
  pending:  'text-brand-light bg-brand/10 border-brand/30',
  approved: 'text-win bg-win/10 border-win/30',
  rejected: 'text-loss bg-loss/10 border-loss/30',
};

const STATUS_ICONS: Record<AccessRequestStatus, React.ElementType> = {
  pending:  Clock,
  approved: CheckCircle,
  rejected: XCircle,
};

function StatusBadge({ status }: { status: AccessRequestStatus }) {
  const Icon = STATUS_ICONS[status];
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5',
        STATUS_STYLES[status],
      )}
    >
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | AccessRequestStatus;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all',      label: 'All' },
  { value: 'pending',  label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export function AdminAccessRequestsPage() {
  const [requests, setRequests]       = useState<AccessRequest[]>([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [success, setSuccess]         = useState<string | null>(null);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = () => {
    setIsLoading(true);
    accessRequestsApi
      .list()
      .then(setRequests)
      .catch((err) => setError((err as Error).message ?? 'Failed to load access requests'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleUpdateStatus = async (id: string, status: AccessRequestStatus) => {
    setActionLoading(id);
    try {
      const updated = await accessRequestsApi.updateStatus(id, status);
      setRequests((prev) => prev.map((r) => r.id === id ? updated : r));
      setSuccess(`Request ${status}.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return requests.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (q && !r.name.toLowerCase().includes(q) && !r.email.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [requests, search, statusFilter]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-100">Access Requests</h2>
          <p className="text-sm text-gray-500 mt-1">
            Early-access submissions from the request-access form.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-3 p-3 bg-win/10 border border-win/20 rounded-lg">
          <CheckCircle className="w-4 h-4 text-win flex-shrink-0" />
          <p className="text-sm text-win">{success}</p>
        </div>
      )}

      {/* Search + filter + refresh */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-8 pr-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/60 transition-colors"
          />
        </div>
        <div className="flex items-center gap-1 bg-surface-2 border border-surface-4 rounded-md p-1">
          <Filter className="w-3.5 h-3.5 text-gray-600 ml-1" />
          {STATUS_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                statusFilter === value
                  ? 'bg-brand/20 text-brand'
                  : 'text-gray-500 hover:text-gray-300',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="p-2 text-gray-500 hover:text-gray-300 hover:bg-surface-3 rounded-md transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 px-5">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading access requests…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-gray-500 gap-2">
            <Inbox className="w-8 h-8 text-gray-700" />
            <p className="text-sm">
              {search || statusFilter !== 'all'
                ? 'No requests match your filters.'
                : 'No access requests yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" role="table">
              <thead>
                <tr className="border-b border-surface-4">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Sport</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Profile</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Wave</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Submitted</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((req) => (
                  <tr
                    key={req.id}
                    className="border-b border-surface-4/50 last:border-0 hover:bg-surface-2/30 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-200 truncate max-w-[160px]">{req.name}</td>
                    <td className="px-4 py-3 text-gray-400 truncate max-w-[200px]">{req.email}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">{req.primary_sport_interest}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">{req.user_profile}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={req.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell">{req.onboarding_wave}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell">{formatDate(req.created_at)}</td>
                    <td className="px-4 py-3">
                      {req.status === 'pending' && (
                        <div className="flex items-center gap-1.5 justify-end">
                          <button
                            onClick={() => handleUpdateStatus(req.id, 'approved')}
                            disabled={actionLoading === req.id}
                            title="Approve"
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-win border border-win/30 bg-win/10 hover:bg-win/20 rounded transition-colors disabled:opacity-50"
                          >
                            <ThumbsUp className="w-3 h-3" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(req.id, 'rejected')}
                            disabled={actionLoading === req.id}
                            title="Reject"
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-loss border border-loss/30 bg-loss/10 hover:bg-loss/20 rounded transition-colors disabled:opacity-50"
                          >
                            <ThumbsDown className="w-3 h-3" />
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-600 text-center">
        {filtered.length} {filtered.length === 1 ? 'request' : 'requests'}
        {requests.length !== filtered.length && ` of ${requests.length}`}
      </p>
    </div>
  );
}
